<template>
    <ModalPopup class="birthplace-form" v-model="isOpen" :closeButton="false" :backdropStatic="true">
        <h2 class="birthplace-form__header">Doplnění povinných údajů</h2>
        <div class="birthplace-form__description">
            S novou podobou <strong>zákona o hazardních hrách</strong> musíme mít nyní u všech hráčů na Sazka.cz
            doplněné <strong>místo a stát narození</strong>. Prosíme vás o vyplnění těchto údajů do políček níže. V
            případě, že nám tyto údaje nedodáte do <strong>1. 3. 2022</strong>, budeme váš účet muset zablokovat.
            <a
                href="/centrum-podpory/online-hrani-na-sazka-cz/muj-ucet/proc-pri-registraci-musim-uvadet-misto-a-stat-narozeni"
                target="_blank"
                >Více informací</a
            >
        </div>
        <div class="birthplace-form__mandatory-note">Všechna pole jsou povinná</div>
        <form class="birthplace-form__form" @submit.prevent="sendForm">
            <div class="birthplace-form__inputs">
                <div class="bs-row birthplace-form__inputs-row">
                    <div class="birthplace-form__input c-6 c-sm-4 c-sm2-6">
                        <label class="birthplace-form__input-label">Město</label>
                        <div class="birthplace-form__input-wrapper">
                            <input
                                required
                                type="text"
                                class="birthplace-form__input-value"
                                name="fname"
                                minlength="2"
                                maxlength="60"
                                v-model="birthplaceForm.city"
                                placeholder="Město narození"
                            />
                        </div>
                    </div>
                    <div class="birthplace-form__input c-6 c-sm-4 c-sm2-6">
                        <label class="birthplace-form__input-label">Země</label>
                        <div class="birthplace-form__input-wrapper selectbox">
                            <SelectBox
                                class="birthplace-form__selectbox"
                                :options="countryOptions"
                                v-model="birthplaceForm.country"
                            >
                            </SelectBox>
                        </div>
                    </div>
                </div>
            </div>
            <div class="birthplace-form__notice">
                Kliknutím na Odeslat potvrzuji, že jsem četl(a)&nbsp;
                <a href="/zpracovani-osobnich-udaju/ochrana-osobnich-udaju" target="_blank" rel="noreferrer noopener"
                    >Informace o zpracování osobních údajů</a
                >
            </div>
            <button type="submit" class="btn btn-primary birthplace-form__btn-submit">Odeslat</button>
        </form>
    </ModalPopup>
</template>

<style scoped lang="scss">
.birthplace-form {
    .modal-dialog {
        @include media-breakpoint-up(sm) {
            max-width: 700px;
        }
    }

    .modal-content {
        border-radius: 8px;
    }

    &__header {
        margin-bottom: 20px;
        letter-spacing: normal;
        text-align: left;
    }

    &__description {
        margin-bottom: 20px;
    }

    &__mandatory-note {
        margin-bottom: 20px;
        color: rgba($clr-neutral-black-900, 0.5);
        text-transform: uppercase;
    }

    &__inputs,
    &__notice {
        margin-bottom: 20px;
    }

    &__inputs-row {
        > :last-child {
            margin-bottom: 0;
        }
    }

    &__input {
        margin-bottom: 20px;

        @include media-breakpoint-up(sm) {
            margin-bottom: 0;
        }
    }

    &__input-value {
        width: 100%;
        min-height: 50px;
        padding: 13px 24px;
        border: 0;
        border-radius: 3px;
        transition: ease-in 200ms outline;

        &:focus,
        &:hover {
            outline: 1px solid $clr-sazka-blue-900;
        }
    }
}
</style>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { useNgSession } from "@sazka/ng/ng-session";
import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { httpClient } from "@sazka/utils/http-clients/http-client";
import { SelectBoxOption } from "@/shared/model/select-box-option";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { sazkaAlert } from "@sazka/utils/sazka-alert";
import ModalPopup from "@/shared/components/modal-popup.vue";
import SelectBox from "@/shared/components/select-box.vue";
import { Analytics } from "@/shared/utils/analytics/analytics";
import { useLocalStorage } from "@sazka/utils/use-storage";

class BirthplaceFormDto {
    city: string;
    country: string;
}

export default defineComponent({
    name: "BirthplaceForm",

    components: {
        ModalPopup,
        SelectBox,
    },

    setup() {
        const { isLoggedIn, session } = useNgSession();
        const storage = useLocalStorage();

        const countryOptions = ref<SelectBoxOption[]>([]);
        const fetchCountries = async () => {
            const response = await httpClient.get("/api/birthplace-form/countries");
            countryOptions.value = response.data;
        };

        const birthplaceForm = ref<BirthplaceFormDto>(new BirthplaceFormDto());
        const isOpen = ref(false);
        const contactGroupName = "BirthplaceForm";
        const localStorageIsFilledKey = "birthplaceIsFilled-";
        const localStorageInGroupKey = "birthplaceInGroup-";

        const setCache = (localStorageKey: string, value: boolean) => {
            storage.setItem(localStorageKey + session.value.PlayerID, value.toString(), "essential");
        };

        const getCache = (localStorageKey: string) => {
            switch (storage.getItem(localStorageKey + session.value.PlayerID)) {
                case "true":
                    return true;
                case "false":
                    return false;
                default:
                    return undefined;
            }
        };

        const isFormFilled = async () => {
            const isFilled = getCache(localStorageIsFilledKey);
            if (isFilled != undefined) {
                return isFilled;
            }

            try {
                const response = await httpClient.get<boolean>(`/api/birthplace-form/is-filled`);
                setCache(localStorageIsFilledKey, response.data);
                if (!response.data) {
                    setCache(localStorageInGroupKey, true);
                }
                return response.data;
            } catch (error) {
                console.error(error);
            }
        };

        const isPlayerInGroup = async () => {
            const inGroup = getCache(localStorageInGroupKey);

            if (inGroup != undefined) {
                return inGroup;
            }

            try {
                const response = await httpClient.get<boolean>(
                    `/api/player/in-group?contactGroupName=${contactGroupName}`
                );

                setCache(localStorageInGroupKey, response.data);
                return response.data;
            } catch (error) {
                console.error(error);
            }
        };

        const showFormQuery = computed(() => {
            return window.location.search.indexOf("doplneni=misto_narozeni") >= 0;
        });

        const isPageExcluded = computed(() => {
            return (
                window.location.pathname.startsWith("/zpracovani-osobnich-udaju") ||
                window.location.pathname.includes("/proc-pri-registraci-musim-uvadet-misto-a-stat-naro")
            );
        });

        const openPopup = async () => {
            await fetchCountries();

            isOpen.value = true;

            dataLayer.push({
                page: {
                    type: "birthPlaceInfo",
                    kind: "",
                    gameCode: Analytics.lotteryCode(),
                    gameCodeStrict: "no_game",
                    verticalCode: Analytics.vertical,
                    verticalCodeStrict: "no_vertical",
                },
                event: "page",
            });
        };

        const closePopup = () => {
            isOpen.value = false;
        };

        const sendForm = async () => {
            Loader.show(100);
            try {
                await httpClient.post("/api/birthplace-form/send", {
                    playerId: session.value.PlayerID,
                    city: birthplaceForm.value.city,
                    country: birthplaceForm.value.country ?? countryOptions.value[0].id,
                });
                setCache(localStorageInGroupKey, false);
                setCache(localStorageIsFilledKey, true);
            } catch (error) {
                sazkaAlert("Chyba při uložení dat", "Zkuste to prosím znovu později");
                console.error(error);
            } finally {
                Loader.hide();
                closePopup();
            }

            dataLayer.push({ event: "action.send.birthPlaceInfo" });
        };

        const openPopupService = () => {
            if (showFormQuery.value) {
                isFormFilled().then((isFilled) => {
                    if (!isFilled && !isPageExcluded.value) {
                        openPopup();
                    }
                });
            } else {
                isPlayerInGroup().then((inGroup) => {
                    if (inGroup && !isPageExcluded.value) {
                        openPopup();
                    }
                });
            }
        };

        const init = async () => {
            if (isLoggedIn.value) {
                openPopupService();
            } else if (showFormQuery.value) {
                await ngFrameworkLoaded();
                ng_fw.showLogin();
            }
        };

        watch(isLoggedIn, () => {
            init();
        });

        init();

        return {
            isOpen,
            birthplaceForm,
            sendForm,
            countryOptions,
        };
    },
});
</script>
